import { Issue as DomainIssue, useIssue } from '@gripp/shared-logic';
import {
  type ColumnData,
  RoutineLabelPickerProvider,
  type RoutineLabelPickerRequest,
  SlideAction,
} from '@gripp/shared-ui';
import {
  type ComponentProps,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { AssetRouteProvider } from '../../assets/assetRouteContext';
import { buildLabelPickerColumn } from '../../assets/components/asset-routine-submit';
import { AssetRoutineSubmitColumn } from '../../assets/components/asset-routine-submit-column';
import { AssetRoutineSubmitHeader } from '../../assets/components/asset-routine-submit-header';

const ASSET_ROUTINE_SUBMIT_COLUMN_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/asset-routine-submit-column.tsx';
const ROUTINE_TITLE_KEY = 'routines.editRoutines.editRoutinesButton';

type AssetRoute = ComponentProps<typeof AssetRouteProvider>['value'];

type ActivityRoutineColumnOptions = {
  issueId?: string;
  routineId?: string;
  /**
   * The conversation the routine sits beside. The routine opens at
   * `<conversationPath>/routine/:routineId`, and back and submit return here.
   */
  conversationPath: string;
  /** Kept on both the open and return URLs (e.g. the asset list's `from`). */
  search?: string;
  /**
   * Asset routing for the routine header, given the activity's asset. Keep it
   * and `debug` stable (useCallback / a constant): the column rebuilds on change.
   */
  assetRoute: (assetId: string) => NonNullable<AssetRoute>;
  /**
   * Host the label picker as its own column. Off for a caller with no slot
   * left for it: the form's label field then opens its inline dropdown.
   */
  labelPickerPanel?: boolean;
  debug: {
    id: string;
    ownerComponent: string;
    ownerSource: string;
    routePanelKey: string;
    routePattern: string;
    routeSource: string;
    routeSymbol: string;
  };
};

/**
 * A routine prompted from an activity's thread (an alert's reminder) opens as
 * a column beside the conversation instead of a modal. The asset comes from
 * the activity, and the activity is passed as the submission's source so
 * submitting resolves it.
 *
 * Returns `onOpenRoutine` for `Issue`, plus the routine column and its label
 * picker column (each `null` while closed, and the picker always `null` with
 * `labelPickerPanel` off) for the caller's slot math.
 */
export const useActivityRoutineColumn = ({
  issueId,
  routineId,
  conversationPath,
  search = '',
  assetRoute,
  labelPickerPanel = true,
  debug,
}: ActivityRoutineColumnOptions) => {
  const navigate = useNavigate();
  const { issue: routineIssue } = useIssue(routineId ? issueId : undefined);
  const routineAssetId = (routineIssue as DomainIssue | undefined)?.asset?.id;
  const routineBackGuardRef = useRef<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >(null);
  const [labelPickerRequest, setLabelPickerRequest] =
    useState<RoutineLabelPickerRequest | null>(null);

  // Callers stay mounted across their sibling routes, so the picker request
  // and the form's registered guard would otherwise outlive the routine they
  // belong to. A layout effect, not a passive one: it must run before the next
  // form's own mount effect registers its guard, or it would clear that too.
  useLayoutEffect(() => {
    routineBackGuardRef.current = null;
    setLabelPickerRequest(null);
  }, [routineId]);

  const onOpenRoutine = useCallback(
    (id: string) =>
      navigate(`${conversationPath}/routine/${id}${search}`, {
        state: { slideLayoutInitialTransition: SlideAction.NEXT },
      }),
    [conversationPath, search, navigate]
  );

  const routineBackButtonGuard = useCallback(
    (): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        if (!routineBackGuardRef.current) {
          resolve(true);
          return;
        }
        routineBackGuardRef.current(
          () => resolve(true),
          () => resolve(false)
        );
      }),
    []
  );

  const routineColumn = useMemo<ColumnData | null>(() => {
    if (!routineId || !issueId || !routineIssue || !routineAssetId) return null;
    const conversationUrl = `${conversationPath}${search}`;
    const form = (
      <AssetRoutineSubmitColumn
        assetId={routineAssetId}
        routineId={routineId}
        issueId={issueId}
        sourceIssue={routineIssue as DomainIssue}
        returnUrl={conversationUrl}
        backGuardRef={routineBackGuardRef}
        animateOnReturn={false}
      />
    );
    return {
      title: ROUTINE_TITLE_KEY,
      showHeader: true,
      showBackButton: true,
      disableScroll: true,
      backButtonTransition: SlideAction.PREV,
      backButtonTransitionBehavior: 'concurrent',
      backButtonGuard: routineBackButtonGuard,
      onBackButtonPress: () =>
        navigate(conversationUrl, {
          state: { slideLayoutInitialTransition: SlideAction.PREV },
        }),
      headerComponent: (
        <AssetRouteProvider value={assetRoute(routineAssetId)}>
          <AssetRoutineSubmitHeader
            assetId={routineAssetId}
            routineId={routineId}
          />
        </AssetRouteProvider>
      ),
      mainComponent: labelPickerPanel ? (
        <RoutineLabelPickerProvider onRequestChange={setLabelPickerRequest}>
          {form}
        </RoutineLabelPickerProvider>
      ) : (
        form
      ),
      debug: {
        id: debug.id,
        titleKey: ROUTINE_TITLE_KEY,
        component: 'AssetRoutineSubmitColumn',
        componentSource: ASSET_ROUTINE_SUBMIT_COLUMN_SOURCE,
        columnSource: debug.ownerSource,
        ownerComponent: debug.ownerComponent,
        ownerSource: debug.ownerSource,
        routePanelKey: debug.routePanelKey,
        routePattern: debug.routePattern,
        routeSource: debug.routeSource,
        source: {
          owner: { file: debug.ownerSource, symbol: debug.ownerComponent },
          column: { file: debug.ownerSource, symbol: 'routineColumn' },
          component: {
            file: ASSET_ROUTINE_SUBMIT_COLUMN_SOURCE,
            symbol: 'AssetRoutineSubmitColumn',
          },
          route: { file: debug.routeSource, symbol: debug.routeSymbol },
        },
        entity: { type: 'routine', id: routineId },
      },
    };
  }, [
    routineId,
    issueId,
    routineIssue,
    routineAssetId,
    conversationPath,
    search,
    assetRoute,
    labelPickerPanel,
    debug,
    routineBackButtonGuard,
    navigate,
  ]);

  const labelPickerColumn = useMemo<ColumnData | null>(
    () =>
      routineColumn
        ? buildLabelPickerColumn(labelPickerRequest, () =>
            setLabelPickerRequest(null)
          )
        : null,
    [routineColumn, labelPickerRequest]
  );

  return { onOpenRoutine, routineColumn, labelPickerColumn };
};
